import { Box, Typography, Chip, Paper, Button, Avatar } from "@mui/material";
import { Business } from "@mui/icons-material";
import { COLORS } from "../theme";
import { useAuth } from "../store/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

// ── Top Nav ───────────────────────────────────────────────────────────────────
export function TopNav() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const crumbs = [
    { label: "Businesses", path: "/businesses" },
  ];
  if (location.pathname.includes("/usage")) {
    crumbs.push({ label: "Usage", path: location.pathname });
  }
  if (location.pathname.includes("/create")) {
    crumbs.push({ label: "Create Usage", path: location.pathname });
  }
  if (location.pathname.includes("/edit")) {
    crumbs.push({ label: "Edit Usage", path: location.pathname });
  }

  return (
    <Box sx={{
      bgcolor: COLORS.card,
      borderBottom: `1px solid ${COLORS.border}`,
      px: 4, py: 0,
      display: "flex", alignItems: "center", gap: 2,
      height: 56, position: "sticky", top: 0, zIndex: 100,
    }}>
      <Box
        onClick={() => navigate("/businesses")}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", mr: 3 }}
      >
        <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: COLORS.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Business sx={{ color: "#fff", fontSize: 16 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Bixjet Admin</Typography>
      </Box>

      {/* Breadcrumbs */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
        {crumbs.map((c, i) => (
          <Box key={c.path} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {i > 0 && <Typography sx={{ color: COLORS.textSub, fontSize: 13 }}>/</Typography>}
            <Typography
              onClick={() => navigate(c.path)}
              sx={{
                fontSize: 13, fontWeight: i === crumbs.length - 1 ? 600 : 400,
                color: i === crumbs.length - 1 ? COLORS.ink : COLORS.muted,
                cursor: "pointer", "&:hover": { color: COLORS.accent },
              }}
            >
              {c.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button size="small" onClick={logout} sx={{ color: COLORS.muted, fontSize: 13, fontWeight: 500 }}>
        Sign out
      </Button>
    </Box>
  );
}

// ── Page wrapper ──────────────────────────────────────────────────────────────
export function PageWrapper({ children }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <TopNav />
      <Box sx={{ maxWidth: 1140, mx: "auto", px: 3, py: 4 }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Page header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.3 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" sx={{ color: COLORS.muted }}>{subtitle}</Typography>}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

// ── Stat card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = COLORS.accent, bg = COLORS.accentLight }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${COLORS.border}`, boxShadow: "none" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>{value}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────
export function StatusChip({ active }) {
  return (
    <Chip
      size="small"
      label={active ? "Active" : "Inactive"}
      sx={{
        bgcolor: active ? COLORS.successLight : COLORS.dangerLight,
        color: active ? COLORS.success : COLORS.danger,
        fontWeight: 700, fontSize: 11,
      }}
    />
  );
}

// ── Plan chip ─────────────────────────────────────────────────────────────────
export function PlanChip({ type }) {
  const map = {
    0: { label: "Free", bg: COLORS.warnLight, color: COLORS.warn },
    1: { label: "Basic", bg: COLORS.accentLight, color: COLORS.accent },
    2: { label: "Pro", bg: "#f0fdf4", color: COLORS.success },
    3: { label: "Enterprise", bg: "#fdf4ff", color: "#9333ea" },
  };
  const cfg = map[type] ?? { label: `Type ${type}`, bg: COLORS.surface, color: COLORS.muted };
  return <Chip size="small" label={cfg.label} sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11 }} />;
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function EmptyState({ icon = "📭", title, desc, action }) {
  return (
    <Box sx={{ textAlign: "center", py: 10 }}>
      <Typography sx={{ fontSize: 48, mb: 2 }}>{icon}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: COLORS.muted, mb: 3 }}>{desc}</Typography>
      {action}
    </Box>
  );
}

// ── Business avatar ───────────────────────────────────────────────────────────
export function BizAvatar({ biz, size = 36 }) {
  const char = (biz?.businessname || biz?.firstname || "?")[0].toUpperCase();
  const hues = [220, 260, 320, 30, 160, 190, 280, 50];
  let h = 0;
  for (const c of (biz?.entityid || "")) h = (h + c.charCodeAt(0)) % hues.length;
  const bg = `hsl(${hues[h]}, 60%, 50%)`;

  return (
    <Avatar sx={{ width: size, height: size, borderRadius: 1.5, bgcolor: bg, fontSize: size * 0.4, fontWeight: 700 }}>
      {biz?.logourl
        ? <img src={biz.logourl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : char}
    </Avatar>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────
export function SectionCard({ children, sx: sxProp }) {
  return (
    <Paper sx={{ border: `1px solid ${COLORS.border}`, boxShadow: "none", overflow: "hidden", ...sxProp }}>
      {children}
    </Paper>
  );
}
